import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { LoginRequest } from '../types';
import { DEFAULT_BRANDING } from '../services/branding';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState<LoginRequest>({
    usernameOrEmail: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(formData);

      if (response.role !== 'ADMIN' && response.role !== 'SUPER_ADMIN') {
        setError('访问被拒绝。需要管理员权限。');
        setLoading(false);
        return;
      }

      login(response.token, response);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || '登录失败。请检查您的用户名和密码。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden gradient-primary">
        {/* Organic background shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-60 h-60 rounded-full bg-white/8 blur-2xl"></div>
          <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full bg-white/3 blur-xl"></div>
        </div>

        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 800 600">
          <defs>
            <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="g2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.04" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.08" />
            </linearGradient>
          </defs>
          <path d="M-50,300 Q200,150 400,250 T800,200 L800,600 L-50,600 Z" fill="url(#g1)" />
          <path d="M-50,400 Q150,300 350,380 T800,320 L800,600 L-50,600 Z" fill="url(#g2)" />
          <path d="M-50,480 Q200,420 450,470 T800,440 L800,600 L-50,600 Z" fill="rgba(255,255,255,0.04)" />
          <circle cx="200" cy="200" r="3" fill="rgba(255,255,255,0.25)" />
          <circle cx="500" cy="120" r="2" fill="rgba(255,255,255,0.2)" />
          <circle cx="600" cy="350" r="3" fill="rgba(255,255,255,0.15)" />
          <circle cx="150" cy="350" r="2" fill="rgba(255,255,255,0.15)" />
          <circle cx="350" cy="450" r="2.5" fill="rgba(255,255,255,0.1)" />
        </svg>

        <div className="relative z-10 flex flex-col items-center justify-center w-full px-16">
          {/* Logo */}
          <div className="mb-10">
            <div className="w-28 h-28 rounded-3xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <span className="material-symbols-outlined text-on-primary" style={{ fontSize: '56px' }}>psychology</span>
            </div>
          </div>

          <h1 className="text-5xl font-bold text-on-primary mb-4 tracking-wider font-headline">{DEFAULT_BRANDING.brandName}</h1>
          <div className="w-16 h-0.5 bg-white/30 rounded mb-5" />
          <p className="text-lg text-white/60 text-center tracking-widest font-body">
            {DEFAULT_BRANDING.brandSubtitle}
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-surface">
        <div className="max-w-sm w-full">
          {/* Mobile header */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-bold text-primary-700 mb-1 font-headline">{DEFAULT_BRANDING.brandName}</h1>
            <p className="text-sm text-on-surface-variant font-body">{DEFAULT_BRANDING.brandSubtitle}</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-on-surface mb-2 font-headline">管理后台</h2>
            <p className="text-on-surface-variant font-body">{`登录以管理${DEFAULT_BRANDING.brandName}`}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-error-50 text-error px-4 py-3 rounded-xl text-sm font-body">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-on-surface mb-1.5 font-label">
                用户名
              </label>
              <input
                id="usernameOrEmail"
                name="usernameOrEmail"
                type="text"
                required
                className="w-full px-4 py-3 bg-surface-container-high rounded-xl text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all focus:bg-surface-container-lowest font-body"
                placeholder="请输入用户名"
                value={formData.usernameOrEmail}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-on-surface mb-1.5 font-label">
                密码
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="w-full px-4 py-3 bg-surface-container-high rounded-xl text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all focus:bg-surface-container-lowest font-body"
                placeholder="请输入密码"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 gradient-primary text-on-primary rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-teal font-body"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
